// Shared API client for the orbital-lint frontend.
//
// Why a single file:
//   - SWR needs a global `fetcher` so cache keys are bare URLs and components
//     don't repeat boilerplate.
//   - All mutating handlers want the same auth + JSON + error shape, so they
//     route through `apiRequest`.
//   - DTO types live next to the client so a backend DTO change forces a
//     compile error at every callsite via TS.
//
// Auth model matches `api/internal/auth.OrgContext`:
//   - If a JWT lives in localStorage (`orbitallint.token` — see lib/auth.ts),
//     send it as `Authorization: Bearer <jwt>`.
//   - Otherwise, if `VITE_DEV_ORG_ID` is defined at build time, send it as
//     `X-Org-Id` so the chi router (started with `AllowDevOrgHeader=true` in
//     docker-compose.dev.yml) can resolve an org scope. Production builds
//     leave the env var unset, so this header is never sent — the backend's
//     `missing_org` 400 becomes a clear UI signal that login is required.
//
// Errors:
//   - 2xx with `{}` body → resolves to `null` (caller-friendly).
//   - 2xx with JSON body → resolves to the parsed body.
//   - non-2xx → throws an `ApiError` with the server's `{code, error}` shape
//     (see handlers/util.go writeErr). Callers can `err instanceof ApiError`
//     to render `err.code` differently from a network failure.

import { getToken, signOut } from './auth';

/**
 * API base URL. Dev defaults to the local API on :8081 (skipping Vite's
 * proxy, which has a known macOS/Node connection issue when targeting a
 * local Go server). Production builds default to same-origin ('') so the
 * SPA hits whatever host serves the bundle. Override with `VITE_API_BASE`
 * in `.env.local` if you serve the API behind a sub-path or a different
 * host.
 *
 * CORS: the API allows `*` in dev (see api/internal/router/router.go), so
 * cross-origin fetch from :5174 → :8081 is permitted without preflight
 * pain. AllowCredentials is false → we send the JWT via Authorization
 * header, never cookies, so the `*` Origin is fine.
 */
export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  (import.meta.env.DEV ? 'http://localhost:8081' : '');

/** Resolve a path against the configured API base. Absolute URLs pass through. */
export function apiURL(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return API_BASE + path;
}

/** Project DTO — mirrors `api/internal/handlers.Project`. */
export interface Project {
  id: string;
  org_id: string;
  slug: string;
  name: string;
  default_branch: string;
  /** Empty string when the backend omits this. JSON omitempty drops it; SWR
   *  responses normalize to `''` so components don't `?.` everywhere. */
  repo_url: string;
  created_at: string;
  /** T254: new-code period type. Empty = PR-diff-only (default). */
  new_code_period_type?: string;
  /** T254: new-code period value (days count or reference branch name). */
  new_code_period_value?: string;
}

/** Body for POST /api/v1/projects. `default_branch` defaults to "main"
 *  server-side when omitted. */
export interface ProjectCreate {
  slug: string;
  name: string;
  default_branch?: string;
  repo_url?: string;
}

/** Branch DTO — mirrors `api/internal/handlers.Branch`. The shape stays
 *  intentionally thin (T031); richer fields (last_scan_at, ahead/behind)
 *  arrive once the corresponding backend tickets land. */
export interface Branch {
  id: string;
  project_id: string;
  name: string;
  is_main: boolean;
  created_at: string;
}

/** Scan DTO — mirrors `api/internal/handlers.Scan`. The Phase-1 backend
 *  only populates id/status/gate_status/created_at on POST, so the rest
 *  are optional here. Once the list endpoint moves off its stub (T031
 *  follow-up), the same DTO covers richer payloads without churn. */
export interface Scan {
  id: string;
  status: string;
  gate_status?: string;
  created_at: string;
  /** OpenTelemetry trace ID for this scan's pipeline run (T654). Empty
   *  when tracing was disabled at scan time. */
  trace_id?: string;
}

/** Issue DTO — designed against the eventual JSON-serialised shape of
 *  `api/internal/db.Issue`. The Phase-1 ListIssuesHandler is a stub that
 *  returns `{items: [], next_cursor: ''}`, so today every field below is
 *  effectively unused at runtime. We still spell the contract out so that
 *  once the handler wires through to `IssuesRepo.List`, the FE just lights
 *  up — no re-shape, no churn, no surprise nullables.
 *
 *  All optional fields use the empty-string / empty-array convention the
 *  rest of this client follows; the backend will likely return them as
 *  omitempty so callers must tolerate both shapes. */
/** One step in a taint source→sink chain (T211/T219). Matches db.FlowStep. */
export interface FlowStep {
  path: string;
  line: number;
  col: number;
  end_line: number;
  end_col: number;
  /** "source" | "propagation" | "sink" — absent for single-step flows. */
  desc?: string;
  /** Containing function name (T219: cross-function flow traces). */
  func_name?: string;
  /** Raw source line at this location (T219: per-node code excerpts). */
  snippet?: string;
}

export interface Issue {
  id: string;
  project_id: string;
  branch_id: string;
  scan_id: string;
  rule_id: string;
  fingerprint: string;
  kind: string;
  severity: string;
  message: string;
  file_path: string;
  line_start: number;
  line_end: number;
  col_start?: number;
  col_end?: number;
  cwe?: number[];
  owasp?: string[];
  effort_min?: number;
  is_new: boolean;
  /** Empty string when unresolved. One of 'fixed','wontfix','falsepositive'. */
  resolution: string;
  detected_at: string;
  /** Ordered source→sink chain for taint findings (T211). Absent for non-taint rules. */
  flow?: FlowStep[];
  /** "external" when the issue was imported via POST /external-issues (T255/T272). */
  source?: string;
  /** T9079: context of the file the finding sits in. Absent = production. */
  path_class?: string;
  /** T9079: set when the context gate downgraded this finding to INFO, e.g. "non-production:test". */
  context_tag?: string;
  /** T9079: severity the finding had before the context gate downgraded it. */
  context_from?: string;
  /** T253: software quality — 'Security' | 'Reliability' | 'Maintainability'. Absent when not classified. */
  software_quality?: string;
  /** T253: clean-code attribute — 'Consistency' | 'Intentionality' | 'Adaptability' | 'Responsibility'. Absent when not classified. */
  clean_code_attribute?: string;
  /** T341: Security Hotspot review status — 'to_review' | 'acknowledged' | 'fixed' | 'safe'.
   *  Only set on issues with kind='SECURITY_HOTSPOT'. */
  hotspot_review_status?: string;
  /** T341: user id of the reviewer who last transitioned this hotspot. Empty until reviewed. */
  hotspot_reviewer_id?: string;
  /** T341: timestamp of the last review transition. Absent until reviewed. */
  hotspot_reviewed_at?: string;
}

/** Response envelope for GET /api/v1/issues. Mirrors
 *  `api/internal/handlers.ListIssuesHandler` and `db.IssueListResult`. */
export interface IssueListResponse {
  items: Issue[];
  /** Empty when the current page is the last one. */
  next_cursor: string;
}

/** Rule DTO — mirrors `api/internal/analyzer.Rule`. The catalog is static
 *  per-build (compiled into the binary via `registry.Default()`), so the FE
 *  loads it once via SWR and looks rules up by ID. The `description` field
 *  is markdown — short paragraphs with inline `code` spans; the issue
 *  detail drawer renders it as the remediation hint.
 *
 *  T256 added `why_it_matters` / `how_to_fix`; T282 added the code-example
 *  and reference fields; T253 added the Clean Code taxonomy fields. */
export interface Rule {
  id: string;
  name: string;
  description: string;
  kind: string;
  severity: string;
  language: string;
  tags?: string[];
  cwe?: number[];
  owasp?: string[];
  effort_min?: number;
  // T950 — size-sensitive remediation cost. Absent/empty remediation_function
  // means the flat effort_min above is the whole story (every rule before
  // T950, and most rules after it); "LINEAR"/"LINEAR_OFFSET" mean effort_min
  // (LINEAR_OFFSET only) plus gap_multiplier_min per gap_unit_label combine
  // to price each issue instance — see api/internal/analyzer.Rule's own doc
  // comment for the exact formula per mode.
  remediation_function?: string;
  gap_multiplier_min?: number;
  gap_unit_label?: string;
  params?: Record<string, string>;
  // T256 — why this rule matters + how to fix it
  why_it_matters?: string;
  how_to_fix?: string;
  // T282 — enriched rule documentation
  noncompliant_example?: string;
  compliant_example?: string;
  references?: string[];
  severity_rationale?: string;
  // T253 — Clean Code taxonomy
  software_quality?: string;
  clean_code_attribute?: string;
  // additional metadata
  is_hotspot?: boolean;
  security_category?: string;
}

/** Response envelope for GET /api/v1/rules?full=1 — the complete, unpaginated
 *  catalog with full documentation on every rule. Mirrors
 *  `api/internal/handlers.ListRulesHandler`'s `full` escape hatch (T838).
 *  Reserved for consumers that genuinely need every rule at once (the
 *  quality-profile activation matrix) or a single already-known rule's full
 *  doc looked up client-side; the search-first /rules page itself uses the
 *  lean, paginated `RuleSearchResponse` below instead. */
export interface RuleListResponse {
  total: number;
  items: Rule[];
}

/** Lean list-view projection of `Rule` — mirrors
 *  `api/internal/handlers.RuleSummary` (T838). Everything `GroupedRuleList`
 *  and the filter chips need, none of the documentation prose that made the
 *  old all-rules response multiple megabytes. */
export interface RuleSummary {
  id: string;
  name: string;
  language: string;
  severity: string;
  kind: string;
  tags?: string[];
  effort_min?: number;
}

/** One value+count pair within a `RuleFacets` bucket. */
export interface RuleFacetCount {
  value: string;
  count: number;
}

/** Per-facet counts for the rules currently matching a query. Mirrors
 *  `api/internal/handlers.RuleFacets`. */
export interface RuleFacets {
  language: RuleFacetCount[];
  severity: RuleFacetCount[];
  kind: RuleFacetCount[];
}

/** Response envelope for GET /api/v1/rules (default, search-first shape).
 *  Mirrors `api/internal/handlers.ListRulesHandler`'s default path (T838).
 *  `items` is intentionally empty when no filter (q/language/severity/kind)
 *  is present — the /rules page renders nothing until the user searches or
 *  picks a facet, so the very first page load only needs `total` + `facets`. */
export interface RuleSearchResponse {
  total: number;
  offset: number;
  limit: number;
  items: RuleSummary[];
  facets: RuleFacets;
}

/** Filter/pagination bag for `rulesApi.search`. */
export interface RuleSearchParams {
  q?: string;
  language?: string;
  severity?: string;
  kind?: string;
  limit?: number;
  offset?: number;
}

/** Canonical severity ladder, INFO → BLOCKER. The order matches the
 *  rendering order used by the issues page chips, and the values match
 *  what `db.IssueFilter.Severity` accepts. */
export const ISSUE_SEVERITIES = [
  'INFO',
  'MINOR',
  'MAJOR',
  'CRITICAL',
  'BLOCKER',
] as const;
export type IssueSeverity = (typeof ISSUE_SEVERITIES)[number];

/** Canonical issue kinds. Matches `db.IssueFilter.Kind`. */
export const ISSUE_KINDS = [
  'BUG',
  'CODE_SMELL',
  'VULNERABILITY',
  'SECURITY_HOTSPOT',
  'SECRET',
] as const;
export type IssueKind = (typeof ISSUE_KINDS)[number];

/** Tri-state new-code filter: 'all' → no filter, 'new' → is_new=true,
 *  'overall' → is_new=false. Mirrors the `IsNew *bool` tri-state on
 *  `db.IssueFilter`. */
export type IssueNewCodeFilter = 'all' | 'new' | 'overall';

/** T9079: context facet. 'production' is the default view; 'all' sends no filter. */
export const ISSUE_CONTEXTS = ['production', 'test', 'fixture', 'generated', 'vendored', 'example', 'all'] as const;
export type IssueContext = (typeof ISSUE_CONTEXTS)[number];

/** Software quality dimension (T253). Matches `db.IssueFilter.SoftwareQuality`. */
export const SOFTWARE_QUALITIES = [
  'Security',
  'Reliability',
  'Maintainability',
] as const;
export type SoftwareQuality = (typeof SOFTWARE_QUALITIES)[number];

/** Clean-code attribute (T253). Matches `db.IssueFilter.CleanCodeAttribute`. */
export const CLEAN_CODE_ATTRIBUTES = [
  'Consistency',
  'Intentionality',
  'Adaptability',
  'Responsibility',
] as const;
export type CleanCodeAttribute = (typeof CLEAN_CODE_ATTRIBUTES)[number];

/** Bag of optional filters used by `issuesApi.list`. project_id is required
 *  because the backend rejects unscoped queries (defense in depth against
 *  cross-project leakage from a stray cursor). */
export interface IssueListParams {
  project_id: string;
  branch_id?: string;
  scan_id?: string;
  severity?: IssueSeverity;
  kind?: IssueKind;
  /** undefined → no filter; true/false → is_new=…. */
  is_new?: boolean;
  /** T9079: restrict to one context class; undefined → no filter. */
  path_class?: string;
  /** T253: filter by software quality dimension. */
  software_quality?: SoftwareQuality;
  /** T253: filter by clean-code attribute. */
  clean_code_attribute?: CleanCodeAttribute;
  /** T648: restrict to an exact set of rule IDs (repeated `rule_id` query
   *  params) — e.g. the Images tab pinning to sca:image-base(-unknown) +
   *  container:vuln without paging past unrelated findings. */
  rule_ids?: string[];
  /** Opaque token from a previous response's next_cursor. */
  cursor?: string;
  /** Page size hint. Backend clamps to [1, db.MaxIssueListLimit=200]. */
  limit?: number;
}

/** Build the canonical /api/v1/issues URL for a given filter set. Exported
 *  so the issues page can pass it as an SWR cache key (keying on the URL
 *  means filter changes invalidate cleanly without a custom keying
 *  function). Stable param order keeps the cache key stable across renders
 *  with the same logical filter. */
export function buildIssuesURL(params: IssueListParams): string {
  const q = new URLSearchParams();
  q.set('project_id', params.project_id);
  if (params.branch_id) q.set('branch_id', params.branch_id);
  if (params.scan_id) q.set('scan_id', params.scan_id);
  if (params.severity) q.set('severity', params.severity);
  if (params.kind) q.set('kind', params.kind);
  if (params.is_new === true) q.set('is_new', 'true');
  if (params.is_new === false) q.set('is_new', 'false');
  if (params.path_class) q.set('path_class', params.path_class);
  if (params.software_quality) q.set('software_quality', params.software_quality);
  if (params.clean_code_attribute) q.set('clean_code_attribute', params.clean_code_attribute);
  for (const ruleID of params.rule_ids ?? []) q.append('rule_id', ruleID);
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.limit !== undefined) q.set('limit', String(params.limit));
  return `/api/v1/issues?${q.toString()}`;
}

/** Error shape from `handlers/util.go writeErr`. */
export interface ApiErrorBody {
  code: string;
  error: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, body: ApiErrorBody) {
    super(body.error || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code || 'unknown';
  }
}

/**
 * Dev-only org id, sourced from `import.meta.env.VITE_DEV_ORG_ID`. The
 * Vite dev server (and `vite build` for staging) substitutes this at
 * compile time. Production builds leave it unset; the resulting `X-Org-Id`
 * header is never sent, so production behavior is identical to a tokenless
 * request against a hardened API.
 */
function devEnv(name: string): string | undefined {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  const v = env?.[name]?.trim();
  return v || undefined;
}

function devOrgID(): string | undefined { return devEnv('VITE_DEV_ORG_ID'); }
function devUserID(): string | undefined { return devEnv('VITE_DEV_USER_ID'); }
function devUserRole(): string | undefined { return devEnv('VITE_DEV_USER_ROLE'); }

function authHeaders(): Record<string, string> {
  const t = getToken();
  if (t) return { Authorization: `Bearer ${t}` };
  const org = devOrgID();
  if (!org) return {};
  const h: Record<string, string> = { 'X-Org-Id': org };
  const uid = devUserID();
  const role = devUserRole();
  if (uid) h['X-User-Id'] = uid;
  if (role) h['X-User-Role'] = role;
  return h;
}

/** Low-level request helper. Components should prefer `useSWR(url, fetcher)`
 *  for reads and `apiRequest` for mutations. */
export async function apiRequest<T = unknown>(
  url: string,
  init: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...authHeaders(),
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(apiURL(url), { ...init, headers });
  const text = await res.text();
  const body = text ? safeJSON(text) : null;
  if (!res.ok) {
    const errBody =
      body && typeof body === 'object' && 'code' in body
        ? (body as ApiErrorBody)
        : { code: 'http_error', error: `HTTP ${res.status}` };
    redirectToLoginOnAuthError(res.status, errBody.code);
    throw new ApiError(res.status, errBody);
  }
  return body as T;
}

/**
 * Bounce to /login when a request fails for lack of a usable session: a
 * `401` (missing/expired/invalid token) or the backend's `missing_org` `400`
 * (no token and no dev-org header resolved a tenant). Reuses `signOut()` so
 * any stale token is cleared before landing on /login, and no-ops if we're
 * already there to avoid a redirect loop while the login page itself calls
 * the unauthenticated `/auth/*\/info` endpoints.
 */
function redirectToLoginOnAuthError(status: number, code: string): void {
  if (status !== 401 && code !== 'missing_org') return;
  if (typeof globalThis.location === 'undefined') return;
  if (globalThis.location.pathname.startsWith('/login')) return;
  signOut();
}

function safeJSON(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** SWR fetcher. Bare URL → JSON body. Throws ApiError on non-2xx so SWR's
 *  `error` is typed and the same as the mutation path. */
export const fetcher = <T = unknown>(url: string): Promise<T> => apiRequest<T>(url);

/** Convenience wrappers — keep callsites at the page level very small. */
/** Sparse-patch body for PATCH /api/v1/projects/{id} (T254). */
export interface ProjectPatch {
  name?: string;
  repo_url?: string;
  default_branch?: string;
  quality_profile_id?: string;
  quality_gate_id?: string;
  /** T254: set to "" to clear (revert to PR-diff-only mode). */
  new_code_period_type?: string;
  new_code_period_value?: string;
}

export const projectsApi = {
  list: (): Promise<Project[]> => apiRequest<Project[]>('/api/v1/projects'),
  get: (id: string): Promise<Project> =>
    apiRequest<Project>(`/api/v1/projects/${encodeURIComponent(id)}`),
  create: (body: ProjectCreate): Promise<Project> =>
    apiRequest<Project>('/api/v1/projects', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patch: (id: string, body: ProjectPatch): Promise<Project> =>
    apiRequest<Project>(`/api/v1/projects/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

export const branchesApi = {
  listByProject: (projectID: string): Promise<Branch[]> =>
    apiRequest<Branch[]>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/branches`,
    ),
};

export const scansApi = {
  listByProject: (projectID: string): Promise<Scan[]> =>
    apiRequest<Scan[]>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/scans`,
    ),
};

export const issuesApi = {
  /** GET /api/v1/issues with the given filter + cursor. Returns the
   *  envelope so callers can decide when to stop paging based on
   *  `next_cursor`. */
  list: (params: IssueListParams): Promise<IssueListResponse> =>
    apiRequest<IssueListResponse>(buildIssuesURL(params)),
};

// ---------------------------------------------------------------------------
// Security Hotspots (T341) — GET /api/v1/hotspots, POST /hotspots/{id}/transition
// ---------------------------------------------------------------------------

/** Canonical hotspot review-status ladder. Matches `db.validHotspotStatuses`. */
export const HOTSPOT_REVIEW_STATUSES = [
  'to_review',
  'acknowledged',
  'fixed',
  'safe',
] as const;
export type HotspotReviewStatus = (typeof HOTSPOT_REVIEW_STATUSES)[number];

/** Bag of filters used by `hotspotsApi.list`. project_id is required —
 *  mirrors `db.HotspotFilter` / `parseHotspotFilter`. */
export interface HotspotListParams {
  project_id: string;
  branch_id?: string;
  /** Filter by review status. Undefined → no filter (all statuses). */
  status?: HotspotReviewStatus;
  security_category?: string;
  /** undefined → no filter; true/false → is_new=…. */
  is_new?: boolean;
  cursor?: string;
  /** Page size hint. Backend clamps to [1, db.MaxHotspotListLimit=200]. */
  limit?: number;
}

/** Response envelope for GET /api/v1/hotspots. Mirrors
 *  `handlers.ListHotspotsHandler` — same shape as `IssueListResponse`. */
export type HotspotListResponse = IssueListResponse;

/** Build the canonical /api/v1/hotspots URL for a given filter set. Exported
 *  so the hotspots page can pass it as an SWR cache key. */
export function buildHotspotsURL(params: HotspotListParams): string {
  const q = new URLSearchParams();
  q.set('project_id', params.project_id);
  if (params.branch_id) q.set('branch_id', params.branch_id);
  if (params.status) q.set('status', params.status);
  if (params.security_category) q.set('security_category', params.security_category);
  if (params.is_new === true) q.set('is_new', 'true');
  if (params.is_new === false) q.set('is_new', 'false');
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.limit !== undefined) q.set('limit', String(params.limit));
  return `/api/v1/hotspots?${q.toString()}`;
}

export const hotspotsApi = {
  /** GET /api/v1/hotspots with the given filter + cursor. */
  list: (params: HotspotListParams): Promise<HotspotListResponse> =>
    apiRequest<HotspotListResponse>(buildHotspotsURL(params)),

  /** POST /api/v1/hotspots/{issueID}/transition — set the review status,
   *  optionally recording a comment authored by the reviewer. Requires a
   *  user session (JWT or dev X-User-Id header); a project token cannot
   *  review hotspots. */
  transition: (
    issueID: string,
    status: HotspotReviewStatus,
    comment?: string,
  ): Promise<{ status: string }> =>
    apiRequest<{ status: string }>(
      `/api/v1/hotspots/${encodeURIComponent(issueID)}/transition`,
      {
        method: 'POST',
        body: JSON.stringify({ status, comment: comment || undefined }),
      },
    ),
};

// ---------------------------------------------------------------------------
// AI-Remediation (T180a/T180c) — POST /api/v1/issues/:id/suggest-fix
// ---------------------------------------------------------------------------

/** Response body from POST /api/v1/issues/:id/suggest-fix. */
export interface SuggestFixResponse {
  issue_id: string;
  /** Unified diff produced by the LLM (may be empty string if no change was suggested). */
  diff: string;
  /** Diff plus the outcome of re-analysing the patched source with our own analyzers (T9084). */
  fix: {
    diff: string;
    verification: FixVerification;
  };
}

/** "failed" fixes arrive with an empty diff — they are never shown. */
export type FixVerificationStatus =
  | 'verified'
  | 'verified_with_side_effect'
  | 'failed'
  | 'unverifiable';

export interface FixVerification {
  status: FixVerificationStatus;
  reason?: string;
  new_findings?: { rule_id: string; message: string; line: number }[];
}

export const llmApi = {
  /** POST /api/v1/issues/:id/suggest-fix — ask the LLM for a fix suggestion.
   *  Sends the issue metadata; the backend fetches source context from S3.
   *  Throws ApiError with status 429 (budget exhausted), 503 (key not
   *  configured), or 502 (upstream LLM failure). */
  suggestFix: async (issue: Issue): Promise<SuggestFixResponse> => {
    // T9364: fetch the ±10-line excerpt so the fix can be verified. A failed
    // fetch (bundle expired, storage unconfigured) degrades to no context,
    // i.e. the fix is reported "unverifiable" rather than the call failing.
    let sourceLines: string[] = [];
    try {
      const src = await apiRequest<{ lines: string[] }>(
        `/api/v1/issues/${encodeURIComponent(issue.id)}/source?project_id=${encodeURIComponent(issue.project_id)}`,
      );
      sourceLines = src.lines ?? [];
    } catch {
      sourceLines = [];
    }
    return apiRequest<SuggestFixResponse>(
      `/api/v1/issues/${encodeURIComponent(issue.id)}/suggest-fix`,
      {
        method: 'POST',
        body: JSON.stringify({
          rule_id: issue.rule_id,
          severity: issue.severity,
          message: issue.message,
          file_path: issue.file_path,
          line_start: issue.line_start ?? 0,
          source_lines: sourceLines,
        }),
      },
    );
  },
};

/** Full-catalog URL (`?full=1`, T838) — exported as a constant so SWR keys
 *  are stable across components. Consumers that need every rule with full
 *  documentation in one shot (the issue/hotspot detail drawers looking up a
 *  known rule_id, the quality-profile activation matrix) hit this same key,
 *  and SWR de-dupes the network round-trip between them. The /rules page
 *  itself uses `buildRulesSearchURL`/`rulesApi.search` instead — the
 *  search-first, paginated, lean-DTO default path. */
export const RULES_CATALOG_URL = '/api/v1/rules?full=1';

/** Build the /api/v1/rules search URL (default, lean/paginated path) for a
 *  given filter + pagination state. Exported so the /rules page can pass it
 *  as a stable SWR(Infinite) cache key. */
export function buildRulesSearchURL(params: RuleSearchParams): string {
  const q = new URLSearchParams();
  if (params.q) q.set('q', params.q);
  if (params.language) q.set('language', params.language);
  if (params.severity) q.set('severity', params.severity);
  if (params.kind) q.set('kind', params.kind);
  if (params.limit !== undefined) q.set('limit', String(params.limit));
  if (params.offset !== undefined) q.set('offset', String(params.offset));
  return `/api/v1/rules?${q.toString()}`;
}

/** Build the GET /api/v1/rules/{ruleID} URL for a single rule's full detail. */
export function buildRuleDetailURL(ruleID: string): string {
  return `/api/v1/rules/${encodeURIComponent(ruleID)}`;
}

// ---------------------------------------------------------------------------
// Search — mirrors search.IssueHit and search.SearchResult from the Go side.
// ---------------------------------------------------------------------------

/** One document returned from GET /api/v1/search/issues. Mirrors
 *  `api/internal/search.IssueHit`. */
export interface IssueHit {
  id: string;
  project_id: string;
  branch_id: string;
  scan_id: string;
  rule_id: string;
  fingerprint: string;
  kind: string;
  severity: string;
  message: string;
  file_path: string;
  line_start: number;
  line_end: number;
  cwe: number[];
  owasp: string[];
  effort_min: number;
  is_new: boolean;
  detected_at: string;
}

/** Envelope from GET /api/v1/search/issues. Mirrors `search.SearchResult`. */
export interface SearchResult {
  total: number;
  from: number;
  size: number;
  hits: IssueHit[];
}

export interface SearchParams {
  q?: string;
  project_id?: string;
  severity?: string;
  kind?: string;
  is_new?: boolean;
  from?: number;
  size?: number;
}

export function buildSearchURL(p: SearchParams): string {
  const qs = new URLSearchParams();
  if (p.q) qs.set('q', p.q);
  if (p.project_id) qs.set('project_id', p.project_id);
  if (p.severity) qs.set('severity', p.severity);
  if (p.kind) qs.set('kind', p.kind);
  if (p.is_new === true) qs.set('is_new', 'true');
  if (p.is_new === false) qs.set('is_new', 'false');
  if (p.from !== undefined && p.from > 0) qs.set('from', String(p.from));
  if (p.size !== undefined) qs.set('size', String(p.size));
  return `/api/v1/search/issues?${qs.toString()}`;
}

export const rulesApi = {
  /** GET /api/v1/rules?full=1 — full rule catalog, static per server-build,
   *  so callers wrap this in `useSWR(RULES_CATALOG_URL, fetcher, { revalidateOnFocus: false })`
   *  to avoid refetching when tabbing back. */
  list: (): Promise<RuleListResponse> =>
    apiRequest<RuleListResponse>(RULES_CATALOG_URL),
  /** GET /api/v1/rules — lean, paginated, faceted search (T838). Used by the
   *  /rules page instead of `list()`. */
  search: (params: RuleSearchParams): Promise<RuleSearchResponse> =>
    apiRequest<RuleSearchResponse>(buildRulesSearchURL(params)),
  /** GET /api/v1/rules/{ruleID} — one rule's full documentation, fetched on
   *  demand when a user opens its detail panel. */
  get: (ruleID: string): Promise<Rule> =>
    apiRequest<Rule>(buildRuleDetailURL(ruleID)),
};

/** Metric counts from the quality-gate summary endpoint. Mirrors
 *  `handlers.QGMetrics` — a projection of the worker's ScanMetrics JSONB
 *  bag that the badge + breakdown UI needs. */
export interface QGMetrics {
  bugs: number;
  vulnerabilities: number;
  code_smells: number;
  security_hotspots: number;
  secrets: number;
  issues_total: number;
  new_bugs: number;
  new_vulnerabilities: number;
  new_code_smells: number;
  new_security_hotspots: number;
  new_secrets: number;
  new_issues_total: number;
  loc: number;
  files_analyzed: number;
  /** T203/T204: overall coverage % (0–100). 0 means no coverage data. */
  coverage?: number;
  lines_to_cover?: number;
  uncovered_lines?: number;
  /** T204: coverage on new code. 100 when no diff/per-line data (gate pass sentinel). */
  new_coverage?: number;
  new_lines_to_cover?: number;
  new_uncovered_lines?: number;
  /** T205/T206: duplication metrics. 0 means no CPD data available. */
  duplicated_lines?: number;
  duplicated_blocks?: number;
  duplicated_files?: number;
  duplicated_lines_density?: number;
  new_duplicated_lines?: number;
  new_duplicated_lines_density?: number;
  /** T528: SQALE technical debt (minutes) + maintainability rating (A-E). */
  technical_debt?: number;
  new_technical_debt?: number;
  sqale_debt_ratio?: number;
  new_sqale_debt_ratio?: number;
  maintainability_rating?: string;
  new_maintainability_rating?: string;
  /** T530: reliability/security ratings (A-E) + blocker-severity counts. */
  reliability_rating?: string;
  new_reliability_rating?: string;
  security_rating?: string;
  new_security_rating?: string;
  blocker_issues?: number;
  new_blocker_issues?: number;
}

/** One configured quality-gate condition alongside the actual metric value
 *  from the evaluated scan (T530). Mirrors `handlers.QualityGateConditionStatus`. */
export interface QualityGateConditionStatus {
  metric: string;
  op: 'GT' | 'GTE' | 'LT' | 'LTE' | 'EQ';
  threshold: number;
  on_new_code: boolean;
  actual: number;
  missing: boolean;
  passed: boolean;
}

/** Response from GET /api/v1/projects/{id}/quality-gate. gate_status values:
 *  "pass" | "fail" | "not_run". gate_name/conditions are omitted when no
 *  gate could be resolved for the project (no explicit binding, no org
 *  default configured, or PG not configured). */
export interface QualityGateSummary {
  gate_status: 'pass' | 'fail' | 'not_run';
  scan_id?: string;
  /** ISO-8601 timestamp — when the scan finished. */
  scanned_at?: string;
  metrics?: QGMetrics;
  gate_name?: string;
  conditions?: QualityGateConditionStatus[];
}

export const qualityGateApi = {
  /** GET /api/v1/projects/{id}/quality-gate — gate status + metric breakdown
   *  for the project's latest succeeded scan. */
  getSummary: (projectID: string): Promise<QualityGateSummary> =>
    apiRequest<QualityGateSummary>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/quality-gate`,
    ),
};

/** Response from GET /api/v1/projects/{id}/quality-profile (T913). Mirrors
 *  `handlers.ProjectQualityProfileResponse`.
 *
 *  active_rule_count / catalog_rule_count is a rule-catalog-coverage proxy,
 *  not a live "findings suppressed" count — the worker only ever computes
 *  findings for the profile's active rules (excluded rules never run), so
 *  there is nothing in the database to count for a rule that was never
 *  activated. profile_id is empty when neither the project nor its org has
 *  a profile configured, in which case active_rule_count equals
 *  catalog_rule_count (the worker's documented "run everything" fallback).
 *
 *  catalog_rule_count is scoped to the resolved profile's own language
 *  (built-in system profiles are per-language, e.g. "Orbital way (Go)") —
 *  comparing a single-language profile against the full ~1100-rule,
 *  ~30-language catalog would make every project look like it suppresses
 *  ~1000 rules regardless of curation quality. */
export interface ProjectQualityProfileSummary {
  profile_id?: string;
  profile_name: string;
  is_system_profile: boolean;
  active_rule_count: number;
  catalog_rule_count: number;
}

export const qualityProfileSummaryApi = {
  /** GET /api/v1/projects/{id}/quality-profile — which profile is bound to
   *  this project, and how much of the built-in rule catalog it activates. */
  getSummary: (projectID: string): Promise<ProjectQualityProfileSummary> =>
    apiRequest<ProjectQualityProfileSummary>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/quality-profile`,
    ),
};

/** One copy of a duplicated code block. */
export interface DuplicatedBlock {
  path: string;
  start_line: number;
  end_line: number;
}

/** A set of identical blocks found across the scanned tree. */
export interface DuplicatedBlockGroup {
  blocks: DuplicatedBlock[];
}

/** Response from GET /api/v1/scans/{scanID}/duplications. */
export interface ScanDuplications {
  duplicated_lines: number;
  duplicated_blocks: number;
  duplicated_files: number;
  duplicated_lines_density: number;
  groups: DuplicatedBlockGroup[];
}

export const scanApi = {
  /** GET /api/v1/scans/{id}/duplications — CPD block-group drilldown. */
  getDuplications: (scanID: string): Promise<ScanDuplications> =>
    apiRequest<ScanDuplications>(
      `/api/v1/scans/${encodeURIComponent(scanID)}/duplications`,
    ),
  /** Download the CycloneDX SBOM for a specific scan (T250). */
  downloadSBOM: (scanID: string): Promise<void> =>
    downloadReport(
      `/api/v1/scans/${encodeURIComponent(scanID)}/sbom`,
      `orbital-lint-sbom-${scanID.slice(0, 8)}.cdx.json`,
    ),
  /** Download the CycloneDX SBOM for a project's latest succeeded scan (T250). */
  downloadProjectSBOM: (projectID: string): Promise<void> =>
    downloadReport(
      `/api/v1/projects/${encodeURIComponent(projectID)}/sbom`,
      `orbital-lint-sbom-project-${projectID.slice(0, 8)}.cdx.json`,
    ),
};

/** PullRequest DTO — mirrors `api/internal/handlers.PullRequestResponse`.
 *  Populated by the SCM provider on PR creation and updated on status changes. */
export interface PullRequest {
  id: string;
  branch_id: string;
  /** One of "github" | "gitlab" | "bitbucket" | "azure". */
  provider: string;
  /** SCM-native PR/MR number or string ID. */
  external_id: string;
  /** One of "open" | "closed" | "merged". */
  status: string;
  /** Empty string when the SCM provider did not supply a title. */
  title: string;
  base_branch: string;
  /** "owner/repo" — required for GitHub; may be empty for other providers. */
  repo_full_name: string;
  created_at: string;
  updated_at: string;
}

export const pullRequestsApi = {
  /** GET /api/v1/projects/{id}/pull-requests — newest first. */
  listByProject: (projectID: string): Promise<PullRequest[]> =>
    apiRequest<PullRequest[]>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/pull-requests`,
    ),
};

// ---------------------------------------------------------------------------
// Portfolios (T065) — mirrors handlers.PortfolioDTO + PortfolioAggregateResp
// ---------------------------------------------------------------------------

/** Portfolio DTO — mirrors `api/internal/handlers.PortfolioDTO`. */
export interface Portfolio {
  id: string;
  org_id: string;
  key: string;
  name: string;
  /** Empty string when omitted server-side. */
  description: string;
  created_at: string;
  updated_at: string;
}

export interface PortfolioCreate {
  key: string;
  name: string;
  description?: string;
}

/** Aggregate metrics for a portfolio. Mirrors
 *  `api/internal/handlers.PortfolioAggregateResp`. */
export interface PortfolioAggregate {
  project_count: number;
  application_count: number;
  gate_pass: number;
  gate_fail: number;
  gate_not_run: number;
  total_bugs: number;
  total_vulnerabilities: number;
  total_code_smells: number;
  total_issues: number;
}

/** Application DTO — mirrors `api/internal/handlers.ApplicationDTO`. */
export interface Application {
  id: string;
  org_id: string;
  key: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Admin users (T084) — mirrors handlers.AdminUser
// ---------------------------------------------------------------------------

/** The five DB-canonical role strings. "developer" is the DB spelling of
 *  RoleDev ("dev" is the Go constant). */
export const USER_ROLES = [
  'reader',
  'developer',
  'maintainer',
  'project_admin',
  'org_admin',
] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** Admin user DTO — mirrors `api/internal/handlers.AdminUser`. */
export interface AdminUser {
  id: string;
  org_id: string;
  email: string;
  name: string;
  role: UserRole;
  created_at: string;
}

export const adminUsersApi = {
  /** GET /api/v1/admin/users — list all users in the caller's org. */
  list: (): Promise<AdminUser[]> =>
    apiRequest<AdminUser[]>('/api/v1/admin/users'),
  /** PATCH /api/v1/admin/users/{id} — update a user's role. */
  updateRole: (id: string, role: UserRole): Promise<AdminUser> =>
    apiRequest<AdminUser>(`/api/v1/admin/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
};

// ---------------------------------------------------------------------------
// Admin audit (T085) — mirrors handlers.AuditEventDTO + AuditListResponse
// ---------------------------------------------------------------------------

/** One audit event row from GET /api/v1/admin/audit. Mirrors
 *  `api/internal/handlers.AuditEventDTO`. */
export interface AuditEvent {
  id: number;
  org_id: string;
  actor_email: string;
  action: string;
  target: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

/** Paginated response from GET /api/v1/admin/audit. */
export interface AuditListResponse {
  items: AuditEvent[];
  /** Empty string when this is the last page. Pass as `before_id` for next page. */
  next_cursor: string;
}

export interface AuditListParams {
  /** Substring search across action, target, actor_email. */
  q?: string;
  /** Exact match on action. */
  action?: string;
  /** Exact match on actor_email. */
  actor?: string;
  /** Cursor from a previous page's next_cursor. */
  before_id?: string;
  /** Page size, default 50, max 200. */
  limit?: number;
}

export function buildAuditURL(p: AuditListParams): string {
  const qs = new URLSearchParams();
  if (p.q) qs.set('q', p.q);
  if (p.action) qs.set('action', p.action);
  if (p.actor) qs.set('actor', p.actor);
  if (p.before_id) qs.set('before_id', p.before_id);
  if (p.limit !== undefined) qs.set('limit', String(p.limit));
  return `/api/v1/admin/audit?${qs.toString()}`;
}

export interface AuditSIEMExportParams {
  /** Return only events with id > since_id. Omitted/0 = from the start. */
  sinceId?: number;
  /** Max events per response, default 1000, max 10000 (server-enforced). */
  limit?: number;
}

/** Builds the URL for GET /api/v1/admin/audit/siem-export (T094). Streams
 *  audit events in ArcSight CEF format, oldest-first — see
 *  `handlers.ExportAuditSIEMHandler`. Gated behind the `siem_export`
 *  license feature (T339); callers should check `LicenseStatus.features`
 *  before surfacing a download control. */
export function buildAuditSIEMExportURL(p: AuditSIEMExportParams = {}): string {
  const qs = new URLSearchParams();
  if (p.sinceId) qs.set('since_id', String(p.sinceId));
  if (p.limit !== undefined) qs.set('limit', String(p.limit));
  const query = qs.toString();
  return `/api/v1/admin/audit/siem-export${query ? `?${query}` : ''}`;
}

export const adminAuditApi = {
  /** GET /api/v1/admin/audit — searchable, paginated audit log. */
  search: (p: AuditListParams): Promise<AuditListResponse> =>
    apiRequest<AuditListResponse>(buildAuditURL(p)),
  /** URL for GET /api/v1/admin/audit/siem-export — pass to `downloadReport`. */
  siemExportURL: buildAuditSIEMExportURL,
};

// ---------------------------------------------------------------------------
// Admin usage (T367) — mirrors handlers.UsageTotalsDTO
// ---------------------------------------------------------------------------

/** Per-period usage totals from GET /api/v1/admin/usage. Mirrors
 *  `api/internal/handlers.UsageTotalsDTO`. Available to every license tier
 *  — not gated behind a `license.Feature`. */
export interface UsageTotals {
  since: string;
  scans_completed: number;
  loc_scanned: number;
  active_projects: number;
  seats: number;
  /** Per-feature usage counters (T621): sso_login, report_exported,
   *  deep_taint_scan, jira_issue_created. Every key is always present,
   *  0 when a feature had no events in the window. */
  feature_counts: Record<string, number>;
}

export interface UsageParams {
  /** Restrict to events recorded in the last N days. Omitted = all-time. */
  sinceDays?: number;
}

function buildUsageURL(base: string, p: UsageParams = {}): string {
  const qs = new URLSearchParams();
  if (p.sinceDays !== undefined) qs.set('since_days', String(p.sinceDays));
  const query = qs.toString();
  return `${base}${query ? `?${query}` : ''}`;
}

export const adminUsageApi = {
  /** GET /api/v1/admin/usage — per-period usage totals for the caller's org. */
  get: (p: UsageParams = {}): Promise<UsageTotals> =>
    apiRequest<UsageTotals>(buildUsageURL('/api/v1/admin/usage', p)),
  /** URL for GET /api/v1/admin/usage/export.csv — pass to `downloadReport`. */
  exportCSVURL: (p: UsageParams = {}): string =>
    buildUsageURL('/api/v1/admin/usage/export.csv', p),
};

// ---------------------------------------------------------------------------
// Admin agents / fleet view (T1015) — mirrors handlers.AgentSummaryDTO
// (api/internal/agentmetrics.Summary)
// ---------------------------------------------------------------------------

/** One ratio plus the sample size (its own denominator). `value` is `null`
 *  on a zero denominator — "not enough data", never a misleading 0%. Always
 *  render `sample_size` next to `value`, per T1015's own framing: an agent
 *  with three scans doesn't have a rate, it has noise. */
export interface AgentMetric {
  value: number | null;
  sample_size: number;
}

/** Four normalized per-agent metrics from GET /api/v1/admin/agents(/{actorID}).
 *  Mirrors `api/internal/agentmetrics.Summary`. */
export interface AgentSummary {
  actor_kind: 'user' | 'project_token';
  actor_id: string;
  window_days: number;
  scan_count: number;
  findings_per_kloc: AgentMetric;
  fix_rate: AgentMetric;
  suppression_rate: AgentMetric;
  reintroduction_rate: AgentMetric;
}

export interface AgentListParams {
  /** Reporting window in days. Omitted = server default (7). */
  windowDays?: number;
}

function buildAgentsURL(base: string, p: AgentListParams = {}): string {
  const qs = new URLSearchParams();
  if (p.windowDays !== undefined) qs.set('window_days', String(p.windowDays));
  const query = qs.toString();
  return `${base}${query ? `?${query}` : ''}`;
}

export const adminAgentsApi = {
  /** GET /api/v1/admin/agents — one row per agent with scan activity in the window. */
  list: (p: AgentListParams = {}): Promise<AgentSummary[]> =>
    apiRequest<AgentSummary[]>(buildAgentsURL('/api/v1/admin/agents', p)),
};

// ---------------------------------------------------------------------------
// Report downloads (T086) — authenticated blob download helper.
// Plain <a href> navigation cannot carry auth headers, so we fetch the report
// as a Blob and trigger the save via a temporary object URL.
// ---------------------------------------------------------------------------

/** Download a report from `url` (resolved against `API_BASE`, same as every
 *  other request — a bare relative `fetch` would hit whatever origin is
 *  serving the SPA instead of the API when they differ) and prompt the
 *  browser to save it as `filename`. */
export async function downloadReport(url: string, filename: string): Promise<void> {
  const res = await fetch(apiURL(url), { headers: { ...authHeaders() } });
  if (!res.ok) {
    const text = await res.text();
    const body = safeJSON(text);
    const errBody =
      body && typeof body === 'object' && 'code' in body
        ? (body as ApiErrorBody)
        : { code: 'http_error', error: `HTTP ${res.status}` };
    throw new ApiError(res.status, errBody);
  }
  const blob = await res.blob();
  const blobURL = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobURL;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobURL);
}

// ---------------------------------------------------------------------------
// Admin settings (T118) — org branding + defaults
// ---------------------------------------------------------------------------

/** Org settings DTO — mirrors `api/internal/handlers.OrgSettingsDTO`. */
export interface OrgSettings {
  id: string;
  slug: string;
  name: string;
  /** Empty string when no logo has been configured. */
  logo_url: string;
  /** Default branch name for new projects (e.g. "main"). */
  default_branch_policy: string;
  /** UUID of the default quality profile, or empty string when not set. */
  default_quality_profile_id: string;
  created_at: string;
  /** T269: org-level fallback new-code period type. Empty = PR-diff-only. */
  default_new_code_period_type: string;
  /** T269: value for the new-code period (days count or branch name). */
  default_new_code_period_value: string;
  /** T269: scan/issue history retention in days. 0 = keep forever. */
  data_retention_days: number;
}

/** Sparse-patch body for PATCH /api/v1/admin/settings. All fields optional. */
export interface OrgSettingsPatch {
  name?: string;
  logo_url?: string;
  default_branch_policy?: string;
  /** Empty string clears the selection (sets NULL). */
  default_quality_profile_id?: string;
  /** T269: empty string resets to PR-diff-only. */
  default_new_code_period_type?: string;
  default_new_code_period_value?: string;
  /** T269: 0 means keep forever. */
  data_retention_days?: number;
}

/** Quality profile list item — mirrors `api/internal/handlers.QualityProfileDTO`. */
export interface QualityProfileItem {
  id: string;
  name: string;
  language: string;
  is_default: boolean;
}

export const adminSettingsApi = {
  /** GET /api/v1/admin/settings — current org settings. */
  get: (): Promise<OrgSettings> =>
    apiRequest<OrgSettings>('/api/v1/admin/settings'),
  /** PATCH /api/v1/admin/settings — update org settings. */
  update: (body: OrgSettingsPatch): Promise<OrgSettings> =>
    apiRequest<OrgSettings>('/api/v1/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /** GET /api/v1/admin/quality-profiles — list org quality profiles for selector. */
  listQualityProfiles: (): Promise<QualityProfileItem[]> =>
    apiRequest<QualityProfileItem[]>('/api/v1/admin/quality-profiles'),
};

// ---------------------------------------------------------------------------
// Admin LLM settings (T180b, T340) — AI fix-suggestion provider config
// ---------------------------------------------------------------------------

/** LLM settings DTO — mirrors `api/internal/handlers.LLMSettingsResponse`. */
export interface LLMSettingsDTO {
  provider: string;
  /** True when an API key is stored server-side; the key itself is never returned. */
  has_api_key: boolean;
  /** Empty string = use the server's built-in default model. */
  model: string;
  daily_token_budget: number;
  requests_per_minute: number;
}

/** Sparse-patch body for PATCH /api/v1/admin/settings/llm. */
export interface LLMSettingsPatch {
  provider?: string;
  /** Write-only. Omit to leave unchanged; pass "" to clear the stored key. */
  api_key?: string;
  /** Empty string resets to the server default. */
  model?: string;
  daily_token_budget?: number;
  requests_per_minute?: number;
}

export const ADMIN_LLM_SETTINGS_KEY = '/api/v1/admin/settings/llm';

export const llmSettingsApi = {
  /** GET /api/v1/admin/settings/llm — current org LLM config. */
  get: (): Promise<LLMSettingsDTO> =>
    apiRequest<LLMSettingsDTO>(ADMIN_LLM_SETTINGS_KEY),
  /** PATCH /api/v1/admin/settings/llm — update org LLM config. */
  update: (body: LLMSettingsPatch): Promise<LLMSettingsDTO> =>
    apiRequest<LLMSettingsDTO>(ADMIN_LLM_SETTINGS_KEY, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

// ---------------------------------------------------------------------------
// Admin quality profiles (T119) — full CRUD + clone
// ---------------------------------------------------------------------------

/** Full quality profile DTO — mirrors `handlers.QualityProfileFullDTO`.
 *  The `rule_count` field is populated by the backend from ListRules so the
 *  list page doesn't need a per-profile round-trip.
 *  T163 added `is_system` (true for built-in Orbital Way / Strict profiles)
 *  and `tier` ("way" | "strict" | "") for built-in tier classification;
 *  T9116 adds "comprehensive" ("Orbital way comprehensive (…)"). */
export interface QualityProfile {
  id: string;
  name: string;
  language: string;
  is_default: boolean;
  rule_count: number;
  is_system: boolean;
  tier: string;
}

export interface QualityProfileCreate {
  name: string;
  language: string;
  is_default?: boolean;
}

export interface QualityProfileUpdate {
  name?: string;
  language?: string;
  is_default?: boolean;
}

export const qualityProfilesApi = {
  list: (): Promise<QualityProfile[]> =>
    apiRequest<QualityProfile[]>('/api/v1/admin/quality-profiles'),
  get: (id: string): Promise<QualityProfile> =>
    apiRequest<QualityProfile>(`/api/v1/admin/quality-profiles/${encodeURIComponent(id)}`),
  create: (body: QualityProfileCreate): Promise<QualityProfile> =>
    apiRequest<QualityProfile>('/api/v1/admin/quality-profiles', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, body: QualityProfileUpdate): Promise<QualityProfile> =>
    apiRequest<QualityProfile>(`/api/v1/admin/quality-profiles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  delete: (id: string): Promise<void> =>
    apiRequest<void>(`/api/v1/admin/quality-profiles/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  /** Clone a profile. Returns the newly-created clone with rule_count matching source. */
  clone: (id: string, name: string): Promise<QualityProfile> =>
    apiRequest<QualityProfile>(`/api/v1/admin/quality-profiles/${encodeURIComponent(id)}/clone`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
};

// ---------------------------------------------------------------------------
// Admin quality profile rules (T120) — rule activation matrix
// ---------------------------------------------------------------------------

/** One rule binding in a quality profile. Mirrors handlers.ProfileRuleDTO.
 *  SeverityOverride="" means "use the rule's default severity". Params holds
 *  any additional rule-specific parameters as string key-value pairs. */
export interface ProfileRule {
  rule_id: string;
  severity_override: string;
  params: Record<string, string>;
}

export const profileRulesApi = {
  /** GET /api/v1/admin/quality-profiles/{id}/rules — active rule bindings. */
  list: (profileID: string): Promise<ProfileRule[]> =>
    apiRequest<ProfileRule[]>(
      `/api/v1/admin/quality-profiles/${encodeURIComponent(profileID)}/rules`,
    ),
  /** PUT /api/v1/admin/quality-profiles/{id}/rules — atomically replace rule set. */
  set: (profileID: string, rules: ProfileRule[]): Promise<ProfileRule[]> =>
    apiRequest<ProfileRule[]>(
      `/api/v1/admin/quality-profiles/${encodeURIComponent(profileID)}/rules`,
      { method: 'PUT', body: JSON.stringify(rules) },
    ),
};

// ---------------------------------------------------------------------------
// Admin quality gates (T121) — CRUD + condition management
// ---------------------------------------------------------------------------

/** Quality gate list item — mirrors handlers.QualityGateDTO. */
export interface QualityGate {
  id: string;
  name: string;
  is_default: boolean;
}

/** Quality gate detail with conditions — mirrors handlers.QualityGateDetailDTO. */
export interface QualityGateDetail {
  id: string;
  name: string;
  is_default: boolean;
  conditions: QualityGateCondition[];
}

/** One gate condition — mirrors handlers.QualityGateConditionDTO. */
export interface QualityGateCondition {
  id: string;
  gate_id: string;
  metric: string;
  /** One of "GT" | "GTE" | "LT" | "LTE" | "EQ". */
  op: string;
  threshold: number;
  on_new_code: boolean;
}

export interface QualityGateCreate {
  name: string;
  is_default?: boolean;
}

export interface QualityGateUpdate {
  name?: string;
  is_default?: boolean;
}

export interface QualityGateConditionCreate {
  metric: string;
  op: string;
  threshold: number;
  on_new_code?: boolean;
}

export interface QualityGateConditionUpdate {
  metric?: string;
  op?: string;
  threshold?: number;
  on_new_code?: boolean;
}

export const adminQualityGatesApi = {
  list: (): Promise<QualityGate[]> =>
    apiRequest<QualityGate[]>('/api/v1/admin/quality-gates'),
  get: (id: string): Promise<QualityGateDetail> =>
    apiRequest<QualityGateDetail>(`/api/v1/admin/quality-gates/${encodeURIComponent(id)}`),
  create: (body: QualityGateCreate): Promise<QualityGate> =>
    apiRequest<QualityGate>('/api/v1/admin/quality-gates', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, body: QualityGateUpdate): Promise<QualityGate> =>
    apiRequest<QualityGate>(`/api/v1/admin/quality-gates/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  delete: (id: string): Promise<void> =>
    apiRequest<void>(`/api/v1/admin/quality-gates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  addCondition: (gateID: string, body: QualityGateConditionCreate): Promise<QualityGateCondition> =>
    apiRequest<QualityGateCondition>(
      `/api/v1/admin/quality-gates/${encodeURIComponent(gateID)}/conditions`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateCondition: (
    gateID: string,
    condID: string,
    body: QualityGateConditionUpdate,
  ): Promise<QualityGateCondition> =>
    apiRequest<QualityGateCondition>(
      `/api/v1/admin/quality-gates/${encodeURIComponent(gateID)}/conditions/${encodeURIComponent(condID)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  deleteCondition: (gateID: string, condID: string): Promise<void> =>
    apiRequest<void>(
      `/api/v1/admin/quality-gates/${encodeURIComponent(gateID)}/conditions/${encodeURIComponent(condID)}`,
      { method: 'DELETE' },
    ),
};

// ---------------------------------------------------------------------------
// Admin SSO (T122) — OIDC + SAML configuration
// ---------------------------------------------------------------------------

/** OIDC provider config. ClientSecret is "**redacted**" when set server-side. */
export interface OIDCConfig {
  enabled: boolean;
  issuer: string;
  client_id: string;
  /** "**redacted**" when a secret is stored; empty string when none set. */
  client_secret: string;
  redirect_url: string;
  email_claim: string;
  name_claim: string;
  role_claim: string;
  role_map: Record<string, string>;
}

/** SAML IdP config. idp_metadata_xml is "**redacted**" when set server-side. */
export interface SAMLConfig {
  enabled: boolean;
  idp_metadata_url: string;
  /** "**redacted**" when stored; empty string when none set. */
  idp_metadata_xml: string;
  acs_url: string;
  email_attr: string;
  name_attr: string;
  role_attr: string;
  role_map: Record<string, string>;
  allow_idp_initiated: boolean;
}

/** LDAP / Active Directory config. BindPassword is "**redacted**" when set server-side. */
export interface LDAPConfig {
  enabled: boolean;
  url: string;
  bind_dn: string;
  /** "**redacted**" when a password is stored; empty string when none set. */
  bind_password: string;
  user_search_base: string;
  user_search_filter: string;
  email_attr: string;
  name_attr: string;
  role_attr: string;
  role_map: Record<string, string>;
  use_tls: boolean;
  skip_cert_verify: boolean;
}

/** Full SSO config response from GET /api/v1/admin/sso. */
export interface SSOConfig {
  oidc: OIDCConfig;
  saml: SAMLConfig;
  ldap: LDAPConfig;
  updated_at: string;
}

/** Sparse OIDC patch. Omit fields to leave them unchanged. */
export interface OIDCConfigPatch {
  enabled?: boolean;
  issuer?: string;
  client_id?: string;
  /** Pass new secret value, or omit (undefined) to leave unchanged.
   *  Sending the literal "**redacted**" is a no-op — the backend ignores it. */
  client_secret?: string;
  redirect_url?: string;
  email_claim?: string;
  name_claim?: string;
  role_claim?: string;
  role_map?: Record<string, string>;
}

/** Sparse SAML patch. */
export interface SAMLConfigPatch {
  enabled?: boolean;
  idp_metadata_url?: string;
  /** Same redaction semantics as OIDCConfigPatch.client_secret. */
  idp_metadata_xml?: string;
  acs_url?: string;
  email_attr?: string;
  name_attr?: string;
  role_attr?: string;
  role_map?: Record<string, string>;
  allow_idp_initiated?: boolean;
}

/** Sparse LDAP patch. */
export interface LDAPConfigPatch {
  enabled?: boolean;
  url?: string;
  bind_dn?: string;
  /** Pass new password, or omit to leave unchanged. "**redacted**" is a no-op. */
  bind_password?: string;
  user_search_base?: string;
  user_search_filter?: string;
  email_attr?: string;
  name_attr?: string;
  role_attr?: string;
  role_map?: Record<string, string>;
  use_tls?: boolean;
  skip_cert_verify?: boolean;
}

export interface SSOConfigPatch {
  oidc?: OIDCConfigPatch;
  saml?: SAMLConfigPatch;
  ldap?: LDAPConfigPatch;
}

export const ADMIN_SSO_KEY = '/api/v1/admin/sso';

export const adminSSOApi = {
  /** GET /api/v1/admin/sso — returns current SSO config (or defaults). */
  get: (): Promise<SSOConfig> => apiRequest<SSOConfig>(ADMIN_SSO_KEY),
  /** PATCH /api/v1/admin/sso — sparse update; returns updated config. */
  patch: (body: SSOConfigPatch): Promise<SSOConfig> =>
    apiRequest<SSOConfig>(ADMIN_SSO_KEY, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

// ---------------------------------------------------------------------------
// Admin personal access tokens (T127) — mirrors handlers.PersonalAccessTokenDTO
// ---------------------------------------------------------------------------

/** One personal access token. `token` is only present in the create response
 *  (copy-once flow); it is absent from list/revoke responses. */
export interface PersonalAccessToken {
  id: string;
  name: string;
  scopes: string[];
  /** ISO-8601 timestamp, absent when the token has never been used. */
  last_used?: string;
  created_at: string;
  revoked: boolean;
  /** Present only in the create (POST) response. Copy once — not stored. */
  token?: string;
}

export interface PersonalAccessTokenCreate {
  name: string;
  scopes: string[];
}

/** Canonical scope strings the server accepts. */
export const PAT_SCOPES = [
  'read:projects',
  'write:scans',
  'read:issues',
  'write:issues',
  'read:rules',
  'read:reports',
  'admin',
] as const;
export type PATScope = (typeof PAT_SCOPES)[number];

export const ADMIN_API_TOKENS_KEY = '/api/v1/admin/api-tokens';

export const adminApiTokensApi = {
  /** GET /api/v1/admin/api-tokens — list caller's tokens (all, including revoked). */
  list: (): Promise<PersonalAccessToken[]> =>
    apiRequest<PersonalAccessToken[]>(ADMIN_API_TOKENS_KEY),
  /** POST /api/v1/admin/api-tokens — create a new token. Returns plaintext once. */
  create: (body: PersonalAccessTokenCreate): Promise<PersonalAccessToken> =>
    apiRequest<PersonalAccessToken>(ADMIN_API_TOKENS_KEY, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** DELETE /api/v1/admin/api-tokens/{id} — revoke a token. */
  revoke: (id: string): Promise<void> =>
    apiRequest<void>(`${ADMIN_API_TOKENS_KEY}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
};

// ---------------------------------------------------------------------------
// Admin integrations (T123) — SCM provider status + health-check
// ---------------------------------------------------------------------------

/** One SCM integration provider's connection status. */
export interface IntegrationProvider {
  provider: 'github' | 'gitlab' | 'azure' | 'bitbucket';
  configured: boolean;
  /** Relative URL to initiate the installation/setup flow. Only present for
   *  GitHub App when the app is not yet configured. */
  setup_url?: string;
}

/** Response from GET /api/v1/admin/integrations. */
export interface AdminIntegrationsResponse {
  providers: IntegrationProvider[];
}

/** Response from POST /api/v1/admin/integrations/{provider}/health-check. */
export interface IntegrationHealthCheck {
  provider: string;
  healthy: boolean;
  latency_ms: number;
  error?: string;
}

export const ADMIN_INTEGRATIONS_KEY = '/api/v1/admin/integrations';

export const adminIntegrationsApi = {
  /** GET /api/v1/admin/integrations — connection status for all SCM providers. */
  get: (): Promise<AdminIntegrationsResponse> =>
    apiRequest<AdminIntegrationsResponse>(ADMIN_INTEGRATIONS_KEY),
  /** POST /api/v1/admin/integrations/{provider}/health-check — live probe. */
  healthCheck: (provider: string): Promise<IntegrationHealthCheck> =>
    apiRequest<IntegrationHealthCheck>(
      `/api/v1/admin/integrations/${encodeURIComponent(provider)}/health-check`,
      { method: 'POST' },
    ),
};

// ---------------------------------------------------------------------------
// Admin webhooks (T124) — outbound webhook CRUD + test-fire + delivery log
// ---------------------------------------------------------------------------

/** One outbound webhook endpoint. Secret is never surfaced in API responses. */
export interface WebhookEndpoint {
  id: string;
  project_id: string;
  url: string;
  events: string[];
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface WebhookEndpointCreate {
  project_id: string;
  url: string;
  secret: string;
  events?: string[];
  enabled?: boolean;
}

export interface WebhookEndpointUpdate {
  enabled?: boolean;
  events?: string[];
}

/** One delivery log entry for a webhook endpoint. */
export interface WebhookDelivery {
  id: string;
  endpoint_id: string;
  delivery_id: string;
  event: string;
  http_status: number;
  latency_ms: number;
  error?: string;
  attempted_at: string;
}

/** Response from POST …/{id}/test-fire. */
export interface WebhookTestFireResult {
  delivery_id: string;
  http_status: number;
  latency_ms: number;
  error?: string;
}

export const ADMIN_WEBHOOKS_KEY = '/api/v1/admin/webhooks';

export const adminWebhooksApi = {
  /** GET /api/v1/admin/webhooks — all endpoints across org projects. */
  list: (): Promise<WebhookEndpoint[]> =>
    apiRequest<WebhookEndpoint[]>(ADMIN_WEBHOOKS_KEY),
  /** POST /api/v1/admin/webhooks — create a new endpoint. */
  create: (body: WebhookEndpointCreate): Promise<WebhookEndpoint> =>
    apiRequest<WebhookEndpoint>(ADMIN_WEBHOOKS_KEY, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** PATCH /api/v1/admin/webhooks/{id} — update enable state or events. */
  update: (id: string, body: WebhookEndpointUpdate): Promise<WebhookEndpoint> =>
    apiRequest<WebhookEndpoint>(`${ADMIN_WEBHOOKS_KEY}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /** DELETE /api/v1/admin/webhooks/{id} — remove the endpoint. */
  delete: (id: string): Promise<void> =>
    apiRequest<void>(`${ADMIN_WEBHOOKS_KEY}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  /** POST /api/v1/admin/webhooks/{id}/test-fire — send a synthetic event. */
  testFire: (id: string): Promise<WebhookTestFireResult> =>
    apiRequest<WebhookTestFireResult>(
      `${ADMIN_WEBHOOKS_KEY}/${encodeURIComponent(id)}/test-fire`,
      { method: 'POST' },
    ),
  /** GET /api/v1/admin/webhooks/{id}/deliveries — recent delivery log. */
  listDeliveries: (id: string): Promise<WebhookDelivery[]> =>
    apiRequest<WebhookDelivery[]>(
      `${ADMIN_WEBHOOKS_KEY}/${encodeURIComponent(id)}/deliveries`,
    ),
};

export const portfoliosApi = {
  list: (): Promise<Portfolio[]> =>
    apiRequest<Portfolio[]>('/api/v1/portfolios'),
  get: (id: string): Promise<Portfolio> =>
    apiRequest<Portfolio>(`/api/v1/portfolios/${encodeURIComponent(id)}`),
  create: (body: PortfolioCreate): Promise<Portfolio> =>
    apiRequest<Portfolio>('/api/v1/portfolios', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  summary: (id: string): Promise<PortfolioAggregate> =>
    apiRequest<PortfolioAggregate>(
      `/api/v1/portfolios/${encodeURIComponent(id)}/summary`,
    ),
  listApplications: (id: string): Promise<Application[]> =>
    apiRequest<Application[]>(
      `/api/v1/portfolios/${encodeURIComponent(id)}/applications`,
    ),
};

// ---------------------------------------------------------------------------
// Admin notifications (T128) — per-user channel preferences + event matrix
// ---------------------------------------------------------------------------

/** Per-user notification preferences returned by GET/PATCH …/preferences. */
export interface NotificationPrefs {
  email_enabled: boolean;
  slack_webhook: string;
  ntfy_topic: string;
  /** Map of event name → subscribed. */
  subscriptions: Record<string, boolean>;
  /** Well-known subscribable event names (from server). */
  events: string[];
}

/** Sparse update body for PATCH …/preferences. All fields optional. */
export interface NotificationPrefsPatch {
  email_enabled?: boolean;
  slack_webhook?: string;
  ntfy_topic?: string;
  subscriptions?: Record<string, boolean>;
}

export const ADMIN_NOTIFICATIONS_PREFS_KEY = '/api/v1/admin/notifications/preferences';

export const adminNotificationsApi = {
  /** GET /api/v1/admin/notifications/preferences — caller's current prefs. */
  getPrefs: (): Promise<NotificationPrefs> =>
    apiRequest<NotificationPrefs>(ADMIN_NOTIFICATIONS_PREFS_KEY),
  /** PATCH /api/v1/admin/notifications/preferences — sparse update. */
  patchPrefs: (body: NotificationPrefsPatch): Promise<NotificationPrefs> =>
    apiRequest<NotificationPrefs>(ADMIN_NOTIFICATIONS_PREFS_KEY, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

// ---------------------------------------------------------------------------
// Admin license (T125) — license-token upload + decoded feature-flag view
// ---------------------------------------------------------------------------

/** License status returned by GET /api/v1/admin/license. */
export interface LicenseStatus {
  /** true when a token has been uploaded (may still be invalid/expired). */
  configured: boolean;
  /** true when the stored token passes signature and expiry checks. */
  valid: boolean;
  /** true when the token sig was OK but the token is past its expiry date. */
  expired: boolean;
  /** Decoded claims — present when valid || expired. */
  licensee?: string;
  tier?: string;
  features?: string[];
  issued_at?: number;
  expires_at?: number;
  /** Set when configured && !valid — the validation failure reason. */
  error?: string;
  /** T620: the same license.Resolve state machine GET /license/state reports
   *  ("grace" | "expired" | "trial" | "enterprise") — distinguishes "no
   *  token uploaded, still within the 14-day grace window" from "no token
   *  uploaded, grace elapsed, writes are blocked", which `configured`/`valid`
   *  alone cannot. */
  access_tier: 'grace' | 'expired' | 'trial' | 'enterprise';
  /** Full days left until the next state change; -1 when there is none. */
  days_remaining: number;
  /** T622: the T614 enforcement inventory rendered live against this org's
   *  current FeatureSet. Always populated, in every access_tier state. */
  enforcement: LicenseEnforcementRow[];
  /** T619: seat-usage summary (active users vs. the license's max_seats
   *  claim). Always populated, even for Community (max_seats 0, state "ok"). */
  seats: LicenseSeats;
}

/** T619 seat-usage summary — see LicenseStatus.seats. */
export interface LicenseSeats {
  /** 0 means the license carries no seat limit. */
  max_seats: number;
  /** Users active (logged in or used a token) in the last 90 days. */
  active_users: number;
  state: 'ok' | 'warning' | 'blocked';
}

/** One row of LicenseStatus.enforcement — see docs/LICENSE-ENFORCEMENT.md. */
export interface LicenseEnforcementRow {
  feature: string;
  /** Whether this org's current license actually grants this feature. */
  licensed: boolean;
  status: 'enforced' | 'partial' | 'not_enforced_deliberate';
  /** Request paths/handlers that actually check this feature today. */
  points?: string[];
  /** What is NOT gated yet — set only when status is "partial". */
  gap?: string;
  /** Ticket that will close the gap — set only when status is "partial". */
  closes_in?: string;
}

export const ADMIN_LICENSE_KEY = '/api/v1/admin/license';

export const adminLicenseApi = {
  /** GET /api/v1/admin/license — current license status + decoded claims. */
  get: (): Promise<LicenseStatus> =>
    apiRequest<LicenseStatus>(ADMIN_LICENSE_KEY),
  /** POST /api/v1/admin/license — upload and verify a new token. */
  upload: (token: string): Promise<LicenseStatus> =>
    apiRequest<LicenseStatus>(ADMIN_LICENSE_KEY, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  /** POST /api/v1/admin/license/start-trial — claim a 30-day self-service trial (T192). */
  startTrial: (email: string, companyName: string, acceptTos: boolean): Promise<LicenseStatus> =>
    apiRequest<LicenseStatus>('/api/v1/admin/license/start-trial', {
      method: 'POST',
      body: JSON.stringify({ email, company_name: companyName, accept_tos: acceptTos }),
    }),
};

// ---------------------------------------------------------------------------
// Admin system (T126) — health dashboard + queue depth + scan-rate sparkline
// ---------------------------------------------------------------------------

/** Response from GET /api/v1/admin/system. Mirrors handlers.SystemStatusDTO. */
export interface SystemStatus {
  /** Maps dep name → "ok" or error string. Absent when health checker not configured. */
  checks?: Record<string, string>;
  /** Maps dep name → reason when intentionally unconfigured (not a failure). */
  disabled?: Record<string, string>;
  /** Current NATS scan queue depth. Absent when NATS is not configured. */
  queue_depth?: number;
  /** 12 five-minute scan-count buckets, oldest first (index 0 = 55–60 min ago). */
  scan_rate_sparkline?: number[];
  /** Advisory-sync freshness (T644). Absent when advisory-sync has never run. */
  advisory_sync?: AdvisorySyncStatus;
}

/** Mirrors handlers.AdvisorySyncStatusDTO (T644). */
export interface AdvisorySyncStatus {
  /** ISO timestamp of the most recent successful sync. Absent if every run failed. */
  last_success_at?: string;
  /** Seconds since last_success_at. Absent alongside last_success_at. */
  age_seconds?: number;
  /** Most recent attempt's fetch mode, regardless of outcome. */
  mode: 'full' | 'delta';
  /** Whether the most recent attempt succeeded. */
  success: boolean;
}

export const ADMIN_SYSTEM_KEY = '/api/v1/admin/system';

export const adminSystemApi = {
  /** GET /api/v1/admin/system — system health status + metrics. */
  get: (): Promise<SystemStatus> =>
    apiRequest<SystemStatus>(ADMIN_SYSTEM_KEY),
};

// ---------------------------------------------------------------------------
// Admin background-tasks (T129) — live scan queue view + retry
// ---------------------------------------------------------------------------

/** One scan entry on the background-tasks dashboard. */
export interface BackgroundTask {
  id: string;
  project_id: string;
  branch_id: string;
  head_commit_sha: string;
  status: 'queued' | 'running' | 'failed';
  error_message?: string;
  started_at?: string;
  finished_at?: string;
  created_at: string;
  /** OpenTelemetry trace ID for this scan's pipeline run (T654). Empty
   *  when tracing was disabled at scan time. */
  trace_id?: string;
}

/** Response from GET /api/v1/admin/background-tasks. */
export interface BackgroundTasksResponse {
  running: BackgroundTask[];
  queued: BackgroundTask[];
  failed: BackgroundTask[];
}

export const ADMIN_BACKGROUND_TASKS_KEY = '/api/v1/admin/background-tasks';

export const adminBackgroundTasksApi = {
  /** GET /api/v1/admin/background-tasks — all active/failed scans in the org. */
  list: (): Promise<BackgroundTasksResponse> =>
    apiRequest<BackgroundTasksResponse>(ADMIN_BACKGROUND_TASKS_KEY),
  /** POST /api/v1/admin/background-tasks/{id}/retry — re-queue a failed scan. */
  retry: (scanID: string): Promise<BackgroundTask> =>
    apiRequest<BackgroundTask>(
      `${ADMIN_BACKGROUND_TASKS_KEY}/${encodeURIComponent(scanID)}/retry`,
      { method: 'POST' },
    ),
};

// ---------------------------------------------------------------------------
// Project permissions (T173/T175) — per-project role grants
// ---------------------------------------------------------------------------

/** The five project-scoped roles. Mirrors db.Role* constants. */
export const PROJECT_PERMISSION_ROLES = [
  'browse',
  'view_source',
  'admin_issues',
  'admin_project',
  'execute_analysis',
] as const;
export type ProjectPermissionRole = (typeof PROJECT_PERMISSION_ROLES)[number];

/** One grant entry returned by GET /api/v1/projects/{id}/permissions.
 *  Mirrors handlers.ProjectGrantEntry — one entry per user, roles aggregated. */
export interface ProjectGrantEntry {
  user_id: string;
  name: string;
  email: string;
  roles: ProjectPermissionRole[];
}

/** Response from GET /api/v1/projects/{id}/my-permissions (T176).
 *  Contains only the roles the authenticated caller holds on the project —
 *  safe to call with browse access, no admin_project required. */
export interface MyProjectPermissions {
  roles: ProjectPermissionRole[];
}

// ---------------------------------------------------------------------------
// Marketplace (T182a/T182b) — catalog + install
// ---------------------------------------------------------------------------

/** One marketplace rule returned by GET /api/v1/marketplace/rules.
 *  Mirrors handlers.MarketplaceRuleDTO. */
export interface MarketplaceRule {
  rule_id: string;
  author: string;
  version: string;
  name: string;
  description: string;
  kind: string;
  severity: string;
  language: string;
  tags: string[];
  downloads: number;
  is_verified: boolean;
  published_at: string;
}

/** Response from GET /api/v1/marketplace/rules. */
export interface MarketplaceRulesResponse {
  rules: MarketplaceRule[];
  total: number;
}

/** Response from POST /api/v1/admin/marketplace/install. */
export interface MarketplaceInstallResponse {
  rule_id: string;
  profile_id: string;
  already_active: boolean;
  is_verified: boolean;
  unverified_override: boolean;
}

export const MARKETPLACE_RULES_URL = '/api/v1/marketplace/rules';

export const marketplaceApi = {
  /** GET /api/v1/marketplace/rules — full marketplace catalog, verified and
   *  unverified rules alike (T652); is_verified on each rule tells them apart. */
  list: (): Promise<MarketplaceRulesResponse> =>
    apiRequest<MarketplaceRulesResponse>(MARKETPLACE_RULES_URL),
  /** POST /api/v1/admin/marketplace/install — install a rule into the org's
   *  Marketplace quality profile. Returns already_active=true if already
   *  installed. Unverified rules are rejected (422) unless `override` is
   *  set — an explicit org-admin acknowledgement (T652) that is recorded
   *  as an audit event server-side. */
  install: (ruleID: string, override = false): Promise<MarketplaceInstallResponse> =>
    apiRequest<MarketplaceInstallResponse>('/api/v1/admin/marketplace/install', {
      method: 'POST',
      body: JSON.stringify({ rule_id: ruleID, override }),
    }),
};

export const projectPermissionsApi = {
  /** GET /api/v1/projects/{id}/permissions — all grants for the project. */
  list: (projectID: string): Promise<ProjectGrantEntry[]> =>
    apiRequest<ProjectGrantEntry[]>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/permissions`,
    ),
  /** GET /api/v1/projects/{id}/my-permissions — caller's own role set (T176). */
  getMyRoles: (projectID: string): Promise<MyProjectPermissions> =>
    apiRequest<MyProjectPermissions>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/my-permissions`,
    ),
  /** POST /api/v1/projects/{id}/permissions/grant — grant one role to a user. */
  grant: (projectID: string, userID: string, role: ProjectPermissionRole): Promise<void> =>
    apiRequest<void>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/permissions/grant`,
      { method: 'POST', body: JSON.stringify({ user_id: userID, role }) },
    ),
  /** POST /api/v1/projects/{id}/permissions/revoke — revoke one role from a user. */
  revoke: (projectID: string, userID: string, role: ProjectPermissionRole): Promise<void> =>
    apiRequest<void>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/permissions/revoke`,
      { method: 'POST', body: JSON.stringify({ user_id: userID, role }) },
    ),
};

// ---------------------------------------------------------------------------
// Project tokens (T197 backend, T705 frontend) — scan-auth tokens scoped to
// a single project. Distinct from personal access tokens (admin/api-tokens,
// user-scoped) and SCIM tokens: this is what `orbital-lint-scanner --token`
// consumes to push scan results for one specific project.
// ---------------------------------------------------------------------------

/** One project_tokens row. `plaintext` is only present in the create response
 *  (copy-once flow); it is absent from list/revoke responses. */
export interface ProjectToken {
  id: string;
  project_id: string;
  name: string;
  /** Present only in the create (POST) response. Copy once — not stored. */
  plaintext?: string;
  /** ISO-8601 timestamp, absent when the token has never been used. */
  last_used?: string;
  created_at: string;
  revoked: boolean;
}

export interface ProjectTokenCreate {
  name: string;
}

export const projectTokensApi = {
  /** GET /api/v1/projects/{id}/tokens — all tokens (active + revoked), newest first. */
  list: (projectID: string): Promise<ProjectToken[]> =>
    apiRequest<ProjectToken[]>(`/api/v1/projects/${encodeURIComponent(projectID)}/tokens`),
  /** POST /api/v1/projects/{id}/tokens — issue a new token. Returns plaintext once. */
  create: (projectID: string, body: ProjectTokenCreate): Promise<ProjectToken> =>
    apiRequest<ProjectToken>(`/api/v1/projects/${encodeURIComponent(projectID)}/tokens`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** DELETE /api/v1/projects/{id}/tokens/{tokenID} — revoke (soft delete). */
  revoke: (projectID: string, tokenID: string): Promise<void> =>
    apiRequest<void>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/tokens/${encodeURIComponent(tokenID)}`,
      { method: 'DELETE' },
    ),
};

// ---------------------------------------------------------------------------
// Admin notification channels (T194a) — org-scoped Slack incoming-webhook CRUD
// ---------------------------------------------------------------------------

/** One org-scoped notification channel. Mirrors handlers.NotificationChannelDTO.
 *  The full webhook URL is never returned — only a masked form (constant `xxxxx…`). */
export interface NotificationChannel {
  id: string;
  org_id: string;
  /** "slack" | "teams" */
  type: string;
  name: string;
  /** Masked webhook URL, e.g. "xxxxx…xxxx". Never the full URL. */
  url_masked: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  /** ISO-8601 timestamp of last attempted delivery. Absent when never fired. */
  last_delivery_at?: string;
  /** Last delivery error message, if any. */
  last_error?: string;
}

export interface NotificationChannelCreate {
  type: 'slack' | 'teams';
  name: string;
  url: string;
}

export interface NotificationChannelPatch {
  enabled?: boolean;
  url?: string;
}

/** Response from POST …/{id}/test. */
export interface NotificationChannelTestResult {
  http_status: number;
  error?: string;
}

export const ADMIN_NOTIFICATION_CHANNELS_KEY = '/api/v1/admin/notification-channels';

export const notificationChannelsApi = {
  /** GET /api/v1/admin/notification-channels — list all channels (optionally filtered by ?type=). */
  list: (type?: string): Promise<NotificationChannel[]> => {
    const url = type
      ? `${ADMIN_NOTIFICATION_CHANNELS_KEY}?type=${encodeURIComponent(type)}`
      : ADMIN_NOTIFICATION_CHANNELS_KEY;
    return apiRequest<NotificationChannel[]>(url);
  },
  /** POST /api/v1/admin/notification-channels — create a new channel. */
  create: (body: NotificationChannelCreate): Promise<NotificationChannel> =>
    apiRequest<NotificationChannel>(ADMIN_NOTIFICATION_CHANNELS_KEY, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** PATCH /api/v1/admin/notification-channels/{id} — toggle enabled or rotate URL. */
  update: (id: string, body: NotificationChannelPatch): Promise<NotificationChannel> =>
    apiRequest<NotificationChannel>(`${ADMIN_NOTIFICATION_CHANNELS_KEY}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /** DELETE /api/v1/admin/notification-channels/{id} — remove a channel. */
  delete: (id: string): Promise<void> =>
    apiRequest<void>(`${ADMIN_NOTIFICATION_CHANNELS_KEY}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  /** POST /api/v1/admin/notification-channels/{id}/test — send a test message. */
  test: (id: string): Promise<NotificationChannelTestResult> =>
    apiRequest<NotificationChannelTestResult>(
      `${ADMIN_NOTIFICATION_CHANNELS_KEY}/${encodeURIComponent(id)}/test`,
      { method: 'POST' },
    ),
};

// ---------------------------------------------------------------------------
// SCIM 2.0 provisioning — T193a
// ---------------------------------------------------------------------------

/** GET /api/v1/admin/scim/status — high-level SCIM status for the org. */
export interface SCIMStatus {
  total_provisioned: number;
  token_count: number;
}

/** A SCIM bearer token row as returned by the admin API (T193a). */
export interface SCIMToken {
  id: string;
  name: string;
  created_at: string;
  last_used_at?: string;
  revoked: boolean;
  /** Plaintext bearer token — only present on the initial POST (copy-once). */
  token?: string;
}

// ---------------------------------------------------------------------------
// Jira integration (T195a)
// ---------------------------------------------------------------------------

/** Mirrors handlers.JiraConfigDTO. APITokenMasked is never the plain token. */
export interface JiraConfig {
  id: string;
  org_id: string;
  base_url: string;
  project_key: string;
  default_issue_type: string;
  api_email: string;
  api_token_masked: string;
  severity_priority_map: Record<string, string>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/** Mirrors handlers.JiraLinkDTO. */
export interface JiraLink {
  orbital_issue_id: string;
  jira_issue_key: string;
  jira_issue_url: string;
  status_at_creation: string;
  created_at: string;
}

export interface JiraBulkResult {
  issue_id: string;
  jira_issue_key?: string;
  jira_issue_url?: string;
  existing?: boolean;
  error?: string;
}

export const ADMIN_JIRA_KEY = '/api/v1/admin/integrations/jira';

export const jiraApi = {
  /** GET /api/v1/admin/integrations/jira — current config (null if none). */
  getConfig: (): Promise<JiraConfig | null> =>
    apiRequest<JiraConfig | null>(ADMIN_JIRA_KEY),

  /** PATCH /api/v1/admin/integrations/jira — upsert with connection test. */
  saveConfig: (body: {
    base_url: string;
    project_key: string;
    default_issue_type?: string;
    api_email: string;
    api_token?: string;
    severity_priority_map?: Record<string, string>;
    enabled?: boolean;
  }): Promise<JiraConfig> =>
    apiRequest<JiraConfig>(ADMIN_JIRA_KEY, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /** DELETE /api/v1/admin/integrations/jira. */
  deleteConfig: (): Promise<void> =>
    apiRequest<void>(ADMIN_JIRA_KEY, { method: 'DELETE' }),

  /** POST /api/v1/admin/integrations/jira/test — test without saving. */
  testConnection: (body: { base_url?: string; api_email?: string; api_token?: string }): Promise<{ ok: boolean; error?: string }> =>
    apiRequest<{ ok: boolean; error?: string }>(`${ADMIN_JIRA_KEY}/test`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** POST /api/v1/issues/{id}/jira — create Jira ticket for one issue. */
  createIssue: (issueID: string, body: { summary?: string; description?: string }): Promise<JiraLink> =>
    apiRequest<JiraLink>(`/api/v1/issues/${encodeURIComponent(issueID)}/jira`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** GET /api/v1/issues/{id}/jira — existing Jira link or null. */
  getLink: (issueID: string): Promise<JiraLink | null> =>
    apiRequest<JiraLink | null>(`/api/v1/issues/${encodeURIComponent(issueID)}/jira`),

  /** POST /api/v1/issues/jira/bulk — create Jira tickets for multiple issues. */
  bulkCreate: (issueIDs: string[]): Promise<{ results: JiraBulkResult[] }> =>
    apiRequest<{ results: JiraBulkResult[] }>('/api/v1/issues/jira/bulk', {
      method: 'POST',
      body: JSON.stringify({ issue_ids: issueIDs }),
    }),
};

// ---------------------------------------------------------------------------
// Scan Measures (T209) — full metrics bag from GET /api/v1/scans/{id}/measures
// ---------------------------------------------------------------------------

/** Full measures response from GET /api/v1/scans/{scanID}/measures.
 *  Mirrors handlers.ScanMeasures — computed by the worker from scans.metrics. */
export interface ScanMeasures {
  files: number;
  files_analyzed: number;
  loc: number;
  loc_raw: number;
  issues_total: number;
  bugs: number;
  vulnerabilities: number;
  code_smells: number;
  security_hotspots: number;
  secrets: number;
  issues_by_severity: Record<string, number>;
  new_issues_total: number;
  new_bugs: number;
  new_vulnerabilities: number;
  new_code_smells: number;
  new_security_hotspots: number;
  new_secrets: number;
  new_issues_by_severity: Record<string, number>;
  lines_to_cover: number;
  uncovered_lines: number;
  coverage: number;
  new_lines_to_cover: number;
  new_uncovered_lines: number;
  new_coverage: number;
  duplicated_lines: number;
  duplicated_blocks: number;
  duplicated_files: number;
  duplicated_lines_density: number;
  new_duplicated_lines: number;
  new_duplicated_lines_density: number;
  complexity: number;
  cognitive_complexity: number;
  technical_debt: number;
  sqale_debt_ratio: number;
  reliability_rating: string;
  security_rating: string;
  maintainability_rating: string;
  new_technical_debt: number;
  new_sqale_debt_ratio: number;
  new_reliability_rating: string;
  new_security_rating: string;
  new_maintainability_rating: string;
  /** T617: scan-time license-enforcement decisions that changed what ran
   *  (e.g. "deep taint skipped: license"). Absent/empty when nothing was skipped. */
  license_notes?: string[];
  /** T700: scan-time completeness caveats that aren't licensing decisions
   *  (e.g. "deep taint incomplete: budget exceeded"). Absent/empty when
   *  every stage ran to completion. */
  analysis_notes?: string[];
}

export const measuresApi = {
  /** GET /api/v1/scans/{scanID}/measures — full metrics bag (T208/T209). */
  getForScan: (scanID: string): Promise<ScanMeasures> =>
    apiRequest<ScanMeasures>(
      `/api/v1/scans/${encodeURIComponent(scanID)}/measures`,
    ),
};

export const ADMIN_SCIM_STATUS_KEY = '/api/v1/admin/scim/status';
export const ADMIN_SCIM_TOKENS_KEY = '/api/v1/admin/scim/tokens';

export const scimApi = {
  getStatus: (): Promise<SCIMStatus> =>
    apiRequest<SCIMStatus>(ADMIN_SCIM_STATUS_KEY),

  listTokens: (): Promise<SCIMToken[]> =>
    apiRequest<SCIMToken[]>(ADMIN_SCIM_TOKENS_KEY),

  createToken: (name: string): Promise<SCIMToken> =>
    apiRequest<SCIMToken>(ADMIN_SCIM_TOKENS_KEY, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  revokeToken: (id: string): Promise<void> =>
    apiRequest<void>(`${ADMIN_SCIM_TOKENS_KEY}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
};

// ─── Rule Templates (T256 / T270) ──────────────────────────────────────────

export interface ParamDefinition {
  name: string;
  description: string;
  required: boolean;
  default?: string;
}

export interface RuleTemplate {
  id: string;
  org_id: string;
  name: string;
  description: string;
  why_it_matters: string;
  how_to_fix: string;
  kind: string;
  severity: string;
  language: string;
  pattern_type: string;
  pattern_template: string;
  param_definitions: ParamDefinition[];
  tags: string[];
  cwe: number[];
  owasp: string[];
  effort_min: number;
  created_at: string;
  updated_at: string;
}

export interface RuleTemplateListResponse {
  total: number;
  items: RuleTemplate[];
}

export interface RuleTemplateCreate {
  name: string;
  description: string;
  why_it_matters?: string;
  how_to_fix?: string;
  kind: string;
  severity: string;
  language: string;
  pattern_type: string;
  pattern_template: string;
  param_definitions?: ParamDefinition[];
  tags?: string[];
  cwe?: number[];
  owasp?: string[];
  effort_min?: number;
}

export interface RuleTemplatePatch {
  name?: string;
  description?: string;
  why_it_matters?: string;
  how_to_fix?: string;
  kind?: string;
  severity?: string;
  language?: string;
  pattern_type?: string;
  pattern_template?: string;
  param_definitions?: ParamDefinition[];
  tags?: string[];
  cwe?: number[];
  owasp?: string[];
  effort_min?: number;
}

export interface ProjectRuleInstance {
  id: string;
  project_id: string;
  template_id: string;
  rule_id: string;
  params: Record<string, string>;
  created_at: string;
}

export const ADMIN_RULE_TEMPLATES_KEY = '/api/v1/admin/rule-templates';

export const ruleTemplatesApi = {
  list: (): Promise<RuleTemplateListResponse> =>
    apiRequest<RuleTemplateListResponse>(ADMIN_RULE_TEMPLATES_KEY),

  create: (body: RuleTemplateCreate): Promise<RuleTemplate> =>
    apiRequest<RuleTemplate>(ADMIN_RULE_TEMPLATES_KEY, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: (id: string, body: RuleTemplatePatch): Promise<RuleTemplate> =>
    apiRequest<RuleTemplate>(`${ADMIN_RULE_TEMPLATES_KEY}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  delete: (id: string): Promise<void> =>
    apiRequest<void>(`${ADMIN_RULE_TEMPLATES_KEY}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  instantiate: (
    projectID: string,
    templateID: string,
    body: { rule_id_suffix: string; params: Record<string, string> },
  ): Promise<ProjectRuleInstance> =>
    apiRequest<ProjectRuleInstance>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/rule-templates/${encodeURIComponent(templateID)}/instantiate`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  listInstances: (projectID: string): Promise<{ total: number; items: ProjectRuleInstance[] }> =>
    apiRequest<{ total: number; items: ProjectRuleInstance[] }>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/rule-instances`,
    ),

  deleteInstance: (projectID: string, instanceID: string): Promise<void> =>
    apiRequest<void>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/rule-instances/${encodeURIComponent(instanceID)}`,
      { method: 'DELETE' },
    ),
};

// ---------------------------------------------------------------------------
// External issue import (T272 / T255)
// ---------------------------------------------------------------------------

/** Supported external report formats — must match externalimport.Format. */
export type ExternalIssueFormat = 'sarif' | 'generic' | 'eslint' | 'rubocop';

export interface ExternalIssueImportResponse {
  scan_id: string;
  imported: number;
}

export const externalIssuesApi = {
  import: (
    projectID: string,
    format: ExternalIssueFormat,
    report: unknown,
    branch?: string,
  ): Promise<ExternalIssueImportResponse> =>
    apiRequest<ExternalIssueImportResponse>(
      `/api/v1/projects/${encodeURIComponent(projectID)}/external-issues`,
      {
        method: 'POST',
        body: JSON.stringify({ format, report, branch: branch || 'main' }),
      },
    ),
};

// ---------------------------------------------------------------------------
// Sibling tools (T294/T295/T296) — admin config + public tools list
// ---------------------------------------------------------------------------

/** One entry from GET /api/v1/tools — only enabled siblings with a URL. */
export interface AvailableTool {
  key: string;
  /** Human-readable label, e.g. "Orbital/Git". */
  label: string;
  base_url: string;
}

/** SWR cache key for GET /api/v1/tools. */
export const TOOLS_KEY = '/api/v1/tools';

/** One sibling-tool config row — mirrors handlers.SiblingToolDTO. */
export interface SiblingToolConfig {
  key: string;
  enabled: boolean;
  /** Empty string when no URL has been set. */
  base_url: string;
  updated_at: string;
  updated_by: string;
}

export const ADMIN_SIBLING_TOOLS_KEY = '/api/v1/admin/sibling-tools';

export const siblingToolsApi = {
  /** GET /api/v1/admin/sibling-tools — both tool rows for the org. */
  list: (): Promise<SiblingToolConfig[]> =>
    apiRequest<SiblingToolConfig[]>(ADMIN_SIBLING_TOOLS_KEY),
  /** PATCH /api/v1/admin/sibling-tools — upsert one tool row.
   *  Returns the full updated list. */
  patch: (key: string, enabled: boolean, base_url: string): Promise<SiblingToolConfig[]> =>
    apiRequest<SiblingToolConfig[]>(ADMIN_SIBLING_TOOLS_KEY, {
      method: 'PATCH',
      body: JSON.stringify({ key, enabled, base_url }),
    }),
};
